import { etDateKey, isDuringMarketHours } from './marketHours';

export const SERIES_MINUTE_MS = 60_000;

const pointTime = (point) => Number(point?.t ?? point?.time);

function compactRanges(ranges = []) {
  return ranges
    .filter((range) => Number.isFinite(range?.from)
      && Number.isFinite(range?.to)
      && range.from < range.to)
    .sort((left, right) => left.from - right.from || left.to - right.to)
    .reduce((result, range) => {
      const previous = result[result.length - 1];
      if (!previous || previous.to < range.from) return [...result, { ...range }];
      previous.to = Math.max(previous.to, range.to);
      const sources = new Set(
        [previous.source, range.source]
          .flatMap((source) => String(source || '').split(', '))
          .filter(Boolean),
      );
      previous.source = [...sources].join(', ') || null;
      return result;
    }, []);
}

function subtractRanges(ranges, coveredRanges) {
  let remaining = compactRanges(ranges);
  for (const covered of compactRanges(coveredRanges)) {
    remaining = remaining.flatMap((range) => {
      if (covered.to <= range.from || covered.from >= range.to) return [range];
      const fragments = [];
      if (range.from < covered.from) fragments.push({ from: range.from, to: covered.from });
      if (covered.to < range.to) fragments.push({ from: covered.to, to: range.to });
      return fragments;
    });
  }
  return remaining;
}

export function replaySeriesGapRanges(
  coverage = {},
  datasets = [],
  cutoffMs = Infinity,
) {
  const boundedCutoffMs = Number.isFinite(cutoffMs) ? cutoffMs : Infinity;
  return compactRanges((datasets || []).flatMap((dataset) => {
    const entry = coverage?.[dataset];
    if (!entry || entry.mode === 'event') return [];
    return subtractRanges(
      entry.missingRanges || [],
      entry.supplementalAvailableRanges || [],
    ).flatMap((range) => {
      const to = Math.min(range.to, boundedCutoffMs + SERIES_MINUTE_MS);
      return to > range.from ? [{ ...range, to, source: dataset }] : [];
    });
  }));
}

export function denseSeriesGapRanges(
  points = [],
  {
    cadenceMs = SERIES_MINUTE_MS,
    toleranceMs = 0,
    getTime = pointTime,
    requireMarketHours = true,
    source = null,
  } = {},
) {
  const times = [...new Set((points || [])
    .map(getTime)
    .map(Number)
    .filter(Number.isFinite)
    .map((time) => Math.floor(time / cadenceMs) * cadenceMs))]
    .sort((left, right) => left - right);
  const ranges = [];
  for (let index = 1; index < times.length; index += 1) {
    const previous = times[index - 1];
    const current = times[index];
    if (current - previous <= cadenceMs + toleranceMs) continue;
    const from = previous + cadenceMs;
    const to = current;
    if (requireMarketHours && (!isDuringMarketHours(from) || !isDuringMarketHours(to))) continue;
    ranges.push({ from, to, source });
  }
  return ranges;
}

export function mergeSeriesGapRanges(...rangeSets) {
  const compacted = compactRanges(rangeSets.flat());
  return compacted.map((range) => {
    const sources = new Set();
    rangeSets.flat().forEach((candidate) => {
      if (candidate?.from < range.to && candidate?.to > range.from && candidate.source) {
        sources.add(candidate.source);
      }
    });
    return { ...range, source: [...sources].join(', ') || null };
  });
}

export function rangeContainsTime(range, time) {
  return Number.isFinite(time) && range?.from <= time && time < range?.to;
}

export function forwardFillSeriesAtGaps(
  points = [],
  gaps = [],
  {
    cadenceMs = SERIES_MINUTE_MS,
    getTime = pointTime,
    withTime = (point, time) => (
      Object.prototype.hasOwnProperty.call(point, 't')
        ? { ...point, t: time }
        : { ...point, time }
    ),
    isValidPrior = (point) => (
      point?.hydrationSeam !== true
      && point?.hydrationBoundaryTail !== true
    ),
    replaceFirstAfterGap = false,
  } = {},
) {
  if (!points.length || !gaps.length) return points;

  const sortedPoints = [...points].sort((left, right) => getTime(left) - getTime(right));
  const byTime = new Map(sortedPoints.map((point) => [Number(getTime(point)), point]));

  for (const gap of compactRanges(gaps)) {
    const prior = sortedPoints.findLast((point) => (
      Number(getTime(point)) < gap.from
      && isValidPrior(point)
    ));
    if (!prior) continue;

    const start = Math.ceil(gap.from / cadenceMs) * cadenceMs;
    for (let time = start; time < gap.to; time += cadenceMs) {
      byTime.set(time, withTime(prior, time));
    }

    if (replaceFirstAfterGap) {
      const recovery = sortedPoints.find((point) => {
        const time = Number(getTime(point));
        return time >= gap.to && time < gap.to + cadenceMs;
      });
      if (recovery) {
        const recoveryTime = Number(getTime(recovery));
        byTime.set(recoveryTime, withTime(prior, recoveryTime));
      }
    }
  }

  return [...byTime.values()].sort((left, right) => getTime(left) - getTime(right));
}

export function seriesGapRangesForDate(ranges = [], epochMs = Date.now()) {
  if (!Number.isFinite(epochMs)) return [];
  const dateKey = etDateKey(epochMs);
  return (ranges || []).filter((range) => (
    Number.isFinite(range?.from) && etDateKey(range.from) === dateKey
  ));
}
